import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'

interface AvailableDevicePickerProps {
  value: string
  onChange: (deviceId: string) => void
}

export function AvailableDevicePicker({ value, onChange }: AvailableDevicePickerProps) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['devices', 'available-picker', debounced],
    queryFn: async () => {
      const params: Record<string, string | number> = { status: 'available', limit: 100, page: 1 }
      if (debounced) params.search = debounced
      const response = await apiClient.get('/devices', { params })
      return (response.data.devices || []).filter((device: any) => device.status === 'available')
    },
  })

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search available serial or model"
          className="h-9 pl-9"
        />
      </div>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : devices.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            No unassigned devices match.
          </p>
        ) : (
          devices.map((device: any) => {
            const selected = value === String(device.id)
            const title = device.deviceModel?.name || device.name
            return (
              <button
                key={device.id}
                type="button"
                onClick={() => onChange(String(device.id))}
                className={cn(
                  'flex w-full items-center justify-between gap-3 border-b border-border/70 px-3 py-2 text-left last:border-b-0',
                  selected ? 'bg-primary/10' : 'hover:bg-muted',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{title}</span>
                  <span className="mt-0.5 block truncate font-mono text-xs text-muted-foreground">
                    {device.serialNumber || 'No serial'}
                    {device.chargerId ? ` · Charger ${device.chargerId}` : ''}
                  </span>
                </span>
                {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
